/**
 * Stocktaking: the shapes stored against a venue, and the arithmetic that
 * turns what somebody sees on the bar into millilitres.
 *
 * The arithmetic itself is `tenthsToMl`, `weighToMl` and `variance` in
 * calc.ts. What lives here is the part above them: resolving an ingredient's
 * bottle size, density and tare — which the prototype does with three lookup
 * tables and a default — and combining a reading of full bottles plus one
 * open bottle into a single figure.
 *
 * The API computes the stored line and the tablet computes the same figure
 * live as somebody types. Both call `countLine`, so the number on the screen
 * and the number in the table cannot disagree.
 */
import { tenthsToMl, variance, weighToMl } from "./calc.js";
/** A bottle of unknown size is assumed to be a 700 — the prototype's default. */
export const DEFAULT_BOTTLE_ML = 700;
/** Density of a spirit, g/mL. Syrups and juices carry their own. */
export const DEFAULT_DENSITY = 0.95;
/** Empty glass, kg, when the bottle size is not one we have weighed. */
export const DEFAULT_TARE_KG = 0.5;
/**
 * Empty-bottle weight by bottle size, kilograms. From the prototype's `TARE`.
 *
 * Tare is a property of the glass, not the liquid, so it keys on size rather
 * than ingredient. An ingredient may still override it — a ceramic amaro
 * bottle weighs nothing like a 700 mL spirit bottle.
 */
export const TARE_BY_BOTTLE_ML = {
    200: 0.18,
    700: 0.5,
    750: 0.52,
    1000: 0.62,
};
export const COUNT_METHODS = ["tenths", "weigh"];
export function isWeighReading(raw) {
    return "grams" in raw;
}
/**
 * Resolve an ingredient's bottle, density and tare, falling back the way the
 * prototype does: 700 mL, 0.95, and tare by bottle size.
 *
 * Zero and negative values fall back rather than being honoured — a bottle of
 * no size divides by nothing useful, and an ingredient imported without these
 * fields arrives as `0` about as often as it arrives as `undefined`. A tare of
 * zero is different and is kept: a bag-in-box has no glass to subtract.
 */
export function bottleSpec(ingredient) {
    const declared = ingredient?.bottleMl;
    const bottleMl = typeof declared === "number" && declared > 0 ? declared : DEFAULT_BOTTLE_ML;
    const dens = ingredient?.density;
    const density = typeof dens === "number" && dens > 0 ? dens : DEFAULT_DENSITY;
    const tare = ingredient?.tareKg;
    const tareKg = typeof tare === "number" && tare >= 0
        ? tare
        : (TARE_BY_BOTTLE_ML[bottleMl] ?? DEFAULT_TARE_KG);
    return { bottleMl, density, tareKg };
}
/** Gross weight of a sealed bottle, kg — what the scale reads before opening. */
export function fullBottleKg(spec) {
    return spec.tareKg + (spec.bottleMl / 1000) * spec.density;
}
/** Millilitres held, from either kind of reading. */
export function readingToMl(raw, spec) {
    if (isWeighReading(raw)) {
        return ((raw.bottles ?? 0) * spec.bottleMl +
            weighToMl(raw.grams, spec.tareKg, spec.density));
    }
    return tenthsToMl(raw.bottles, raw.tenths, spec.bottleMl);
}
/** Round to one decimal place. Stored figures are millilitres, not float dust. */
function round1(n) {
    return Math.round(n * 10) / 10;
}
/**
 * One finished count line: what was counted, what was expected, and by how
 * much they differ.
 *
 * Variance is taken from the rounded figure, not the raw one, so the band
 * shown next to `-118.4 mL` is the band for `-118.4 mL`. A line that rounds
 * across a threshold should be coloured by the number the reader can see.
 */
export function countLine(input) {
    const countedMl = round1(readingToMl(input.raw, input.spec));
    const expectedMl = round1(input.expectedMl);
    const v = variance(countedMl, expectedMl);
    return {
        countId: input.countId,
        ingredientId: input.ingredientId,
        countedMl,
        expectedMl,
        method: input.method,
        raw: input.raw,
        deltaMl: round1(v.deltaMl),
        percent: Math.round(v.percent * 100) / 100,
        band: v.band,
    };
}
//# sourceMappingURL=stock.js.map